'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconDoc, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

// Ankara landing page için ürün bilgileri
const ankProduct: Product = {
  slug: 'ankara-bondex-sunger',
  name: 'Ankara Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Ankara bondex sünger; şehir içi hızlı teslimat, proje bazlı keşif ve akustik danışmanlıkla rebonded süngerpan tedariki sunar.',
  description:
    'Ankara bondex sünger; yüksek yoğunluklu rebonded süngerpan yapısıyla darbe ve titreşim seslerini azaltmaya yardımcı olan, Ankara içi projelere özel kurgulanmış bir ses yalıtım süngeri çözümüdür. Konut, ofis, stüdyo ve endüstriyel projelerde kullanılır.',
  imagePath: '/images/bondex-sunger.webp',
  sku: 'BDX-ANK',
  mpn: 'BDX-ANK',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Ankara bondex sünger nedir?',
      answer:
        'Ankara bondex sünger; rebonded süngerpan yapısına sahip, Ankara içi projelere özel kurgulanmış yüksek yoğunluklu bir ses yalıtım ve titreşim sönümleme çözümüdür. Konut, ofis, stüdyo ve endüstriyel projelerde kullanılır.',
    },
    {
      question: 'Ankara içinde bondex sünger teslimat süresi nedir?',
      answer:
        'Stoktan sevk edilen Ankara bondex sünger siparişleri, Ankara içinde genellikle 1–3 iş günü içinde teslim edilir. Talebe göre aynı gün veya hafta sonu teslimat/uygulama opsiyonları proje bazlı planlanabilir.',
    },
    {
      question: 'Ankara bondex sünger hangi kalınlıklarda var?',
      answer:
        `Ankara için ana stok kalınlıkları 10mm, 20mm, 30mm, 40mm ve 50mm'dir. İnce detaylar için 10–20mm, daha yüksek izolasyon hedeflerinde 30–50mm ve gerekiyorsa bariyerli/çift bariyerli sistemler önerilir.`,
    },
    {
      question: `Ankara'da keşif veya uygulama desteği veriyor musunuz?`,
      answer:
        `Ankara içi projelerde yerinde keşif, katman kurgusu ve uygulama ekibi yönlendirmesi sağlanabilir. Proje metrajı ve hedef izolasyon seviyesine göre keşif takvimi ve uygulama planı birlikte oluşturulur.`,
    },
    {
      question: 'Ankara bondex sünger fiyatı ne kadar?',
      answer:
        `Ankara bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Fiyat; seçilen kalınlık, toplam metraj ve katman kurgusuna göre değişir. Net fiyat için proje detaylarınızı WhatsApp üzerinden paylaşmanız yeterlidir.`,
    },
  ],
  reviews: [
    {
      authorName: 'Can Yıldız',
      datePublished: '2025-12-10',
      ratingValue: 5,
      title: 'Çankaya konut projesinde başarılı sonuç',
      body:
        `Ankara'daki konut projemizde daire zeminlerinde bondex sünger kullandık. Katlar arası darbe sesi belirgin şekilde azaldı, teslimat ve uygulama planı şehir şartlarına rağmen sorunsuz yönetildi.`,
      verified: true,
    },
    {
      authorName: 'Murat Özkan',
      datePublished: '2025-12-14',
      ratingValue: 5,
      title: 'Söğütözü ofisinde konforlu akustik',
      body:
        'Söğütözü ofisinde alçıpan ara bölmelerde ve makine odası çevresinde Ankara bondex sünger kullandık. Ortam gürültüsü ve titreşimler kontrol altına alındı, ekip teknik detaylarda oldukça destek oldu.',
      verified: true,
    },
    {
      authorName: 'Serkan Aydın',
      datePublished: '2025-12-18',
      ratingValue: 4,
      title: 'Stüdyo için doğru kombinasyon',
      body:
        `Ankara'daki müzik stüdyomuzda bondex sünger, bariyerli bondex ve akustik panelleri birlikte kullandık. Dışarıya ses sızıntısı azaldı, içeride de daha kontrollü bir akustik ortam oluştu.`,
      verified: true,
    },
    {
      authorName: 'Burak Kaya',
      datePublished: '2025-12-22',
      ratingValue: 5,
      title: 'Jeneratör odasında mükemmel sonuç',
      body:
        `Yenimahalle'deki iş merkezimizin jeneratör odasında bondex sünger ve bariyerli bondex kullandık. Makine titreşimleri ve gürültü önemli ölçüde azaldı, komşu ofislerden şikayet almıyoruz artık.`,
      verified: true,
    },
    {
      authorName: 'Emre Demir',
      datePublished: '2025-12-25',
      ratingValue: 5,
      title: 'Hızlı teslimat ve kaliteli ürün',
      body:
        `Keçiören'deki apartman projemizde 180 m² bondex sünger sipariş ettik. Ankara içi teslimat 2 günde gerçekleşti, ürün kalitesi ve kesim hassasiyeti beklentilerin üzerindeydi.`,
      verified: true,
    },
    {
      authorName: 'Ahmet Şahin',
      datePublished: '2026-01-05',
      ratingValue: 5,
      title: 'Home ofis için ideal çözüm',
      body:
        `Evdeki çalışma odamda bondex sünger 20mm kullandım. Salon ve çocuk odasından gelen sesler artık rahatsız etmiyor, online toplantılarda ses kalitesi çok iyi. Uygulama da çok kolaydı.`,
      verified: true,
    },
  ],
};

export function CityBondexAnkara() {
  const url = toAbsoluteUrl('/ankara-bondex-sunger');

  // SEO için sabit title ve description
  const title = 'Ankara Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Ankara bondex sünger, yüksek yoğunluklu yapısıyla darbe ve titreşim seslerini azaltır. Akustik yalıtım süngeri için üretici Echopan kalitesiyle sunulur.';

  // Ankara landing için, belirli ürün sayfalarına güçlü iç link amaçlı sabit liste
  const relatedSlugs = [
    'bondex-sunger',
    'bondex-sunger-10mm',
    'bondex-sunger-20mm',
    'bondex-sunger-30mm',
    'bondex-sunger-40mm',
    'bariyerli-bondex-sunger',
    'cift-bariyerli-bondex-sunger',
  ];

  const relatedBondex = relatedSlugs
    .map((slug) => products.find((p) => p.slug === slug))
    .filter((p): p is Product => Boolean(p));

  // WhatsApp URL
  const waMessage = `Merhaba, Ankara bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (ankara-bondex-sunger)`;
  const waUrl = buildWhatsAppUrl(waMessage);

  // Fiyat formatı
  const formatPrice = (value: number) => {
    try {
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
    } catch {
      return `${value} TL`;
    }
  };
  const displayPrice = ankProduct.priceTRY ? formatPrice(ankProduct.priceTRY) : null;

  // Yorumlar
  const pageReviews = ankProduct.reviews?.length
    ? ankProduct.reviews.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    }))
    : [];

  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((s, r) => s + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  // Özellikler grid
  const topSpecs = [
    { k: 'Stok', v: 'Ankara içi düzenli stok' },
    { k: 'Garanti', v: 'Kurumsal' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kurulum', v: 'Ücretsiz kurulum desteği' },
    { k: 'Kalınlıklar', v: '10mm, 20mm, 30mm, 40mm, 50mm' },
    { k: 'Hizmet Bölgesi', v: 'Ankara geneli (Merkez + Tüm ilçeler)' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Süre', v: '1 gün' },
  ];

  // SEO için breadcrumb
  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Ankara Bondex Sünger', item: url },
  ];

  // priceValidUntil (1 yıl sonrası)
  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  // Schema.org JSON-LD - ProductSEO ile aynı yapı
  const schemaGraph: any[] = [
    // 1) WebSite Schema
    {
      '@type': 'WebSite',
      '@id': `${toAbsoluteUrl('/')}#website`,
      name: 'Bondex Sünger',
      url: toAbsoluteUrl('/'),
      inLanguage: 'tr-TR',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    // 2) Organization Schema
    {
      '@type': 'Organization',
      '@id': `${toAbsoluteUrl('/')}#organization`,
      name: 'bariyerlibondexsunger.com',
      url: toAbsoluteUrl('/'),
      logo: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl('/images/bondex-logo.webp'),
        width: 512,
        height: 512,
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+90 216 519 34 11',
          contactType: 'customer service',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
        {
          '@type': 'ContactPoint',
          telephone: '+90 546 919 11 34',
          contactType: 'sales',
          areaServed: 'TR',
          availableLanguage: ['tr'],
        },
      ],
      sameAs: [
        'https://www.facebook.com/profile.php?id=61575206163572',
        'https://x.com/KabinPods',
        'https://www.instagram.com/kabinpods/',
        'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
        'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
      ],
    },
    // 3) WebPage Schema
    {
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: title,
      description,
      isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
      breadcrumb: { '@id': `${url}#breadcrumb` },
      inLanguage: 'tr-TR',
      datePublished: '2025-12-17',
      dateModified: new Date().toISOString().split('T')[0],
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: toAbsoluteUrl(ankProduct.imagePath),
        width: 1200,
        height: 750,
      },
      about: [
        { '@type': 'Thing', name: 'Bondex Sünger' },
        { '@type': 'Thing', name: 'Ankara Bondex Sünger' },
        { '@type': 'Thing', name: 'ses yalıtım süngeri' },
        { '@type': 'Thing', name: 'akustik sünger' },
        { '@type': 'Thing', name: 'izolasyon süngeri' },
        { '@type': 'Thing', name: 'Ankara ses yalıtımı' },
      ],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.ProductSEO', '.ProductPage h1'],
      },
      mainEntity: { '@id': `${url}#product` },
    },
    // 4) BreadcrumbList Schema
    {
      '@type': 'BreadcrumbList',
      '@id': `${url}#breadcrumb`,
      itemListElement: breadcrumb.map((b, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: b.name,
        item: b.item,
      })),
    },
    // 5) Product Schema
    {
      '@type': 'Product',
      '@id': `${url}#product`,
      name: ankProduct.name,
      image: [
        {
          '@type': 'ImageObject',
          url: toAbsoluteUrl(ankProduct.imagePath),
          width: 1200,
          height: 750,
        },
      ],
      description: ankProduct.description,
      brand: { '@type': 'Brand', name: 'Bondex' },
      category: ankProduct.category,
      url,
      sku: ankProduct.sku,
      mpn: ankProduct.mpn,
      productID: ankProduct.sku || ankProduct.mpn,
      material: 'Rebonded Süngerpan',
      audienceType: 'Business',
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Kategori',
          value: ankProduct.category,
        },
        {
          '@type': 'PropertyValue',
          name: 'Tip',
          value: 'Rebonded Süngerpan',
        },
        {
          '@type': 'PropertyValue',
          name: 'Hizmet Bölgesi',
          value: 'Ankara',
        },
      ],
      offers: {
        '@type': 'Offer',
        url,
        priceCurrency: 'TRY',
        price: String(ankProduct.priceTRY),
        validFrom: validFromStr,
        priceValidUntil: priceValidUntilStr,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
        unitText: 'm²',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: String(ankProduct.priceTRY),
          priceCurrency: 'TRY',
          unitCode: 'MTK',
          unitText: 'm²',
        },
        hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
        shippingDetails: { '@id': `${url}#shippingDetails` },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: avgRating.toFixed(1),
        reviewCount: pageReviews.length,
        bestRating: '5',
        worstRating: '1',
      },
      review: ankProduct.reviews?.map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.authorName },
        datePublished: r.datePublished,
        reviewBody: r.body,
        headline: r.title,
        ...(r.verified && { reviewAspect: 'Verification' }),
        reviewRating: {
          '@type': 'Rating',
          ratingValue: String(r.ratingValue),
          bestRating: '5',
          worstRating: '1',
        },
      })),
    },
    // 6) MerchantReturnPolicy Schema
    {
      '@type': 'MerchantReturnPolicy',
      '@id': `${url}#merchantReturnPolicy`,
      applicableCountry: {
        '@type': 'Country',
        name: 'TR',
      },
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    },
    // 7) OfferShippingDetails Schema
    {
      '@type': 'OfferShippingDetails',
      '@id': `${url}#shippingDetails`,
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency: 'TRY',
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: {
          '@type': 'Country',
          name: 'TR',
        },
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 3,
          unitCode: 'DAY',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 5,
          unitCode: 'DAY',
        },
        businessDays: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        },
        cutoffTime: '14:00',
      },
    },
    // 8) FAQPage Schema
    {
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: ankProduct.faqs?.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': schemaGraph,
  };

  // Tab içerikleri - 700-900 kelime arası zengin içerik
  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ankara Bondex Sünger Nedir?</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Rebonded Süngerpan Teknolojisi</h3>
            <p className={styles.seoText}>
              <strong>Ankara bondex sünger</strong>; rebonded süngerpan yapısına sahip, Ankara ili ve ilçelerindeki projelere özel
              kurgulanmış yüksek yoğunluklu bir <strong>ses yalıtım ve titreşim sönümleme</strong> çözümüdür. Geri dönüştürülmüş sünger
              parçacıklarının preslenip bağlanmasıyla elde edilen bu malzeme, katlar arası darbe seslerini, makine dairesi titreşimlerini
              ve şehir gürültüsünü kontrol altına almak için kullanılır.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Ankara İçin Özel Çözümler</h3>
            <p className={styles.seoText}>
              Ankara'nın geniş yapı stoğunda; komşu daireler arası ses geçişi, açık ofis gürültüsü, apartman koridor sesleri ve endüstriyel
              hacimlerden gelen titreşimler için proje bazlı katman kurguları ile uygulanır.{' '}
              <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> ailesinin tüm kalınlık ve modelleri Ankara içi stoktan
              tedarik edilebilir. Rebonded teknolojisi sayesinde hem çevre dostu hem de yüksek performanslı bir akustik çözüm sunar.
            </p>
          </section>

          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ankara'da Bondex Sünger Kullanım Alanları</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Konut ve Apartman Projeleri</h3>
            <p className={styles.seoText}>
              Üst kat komşu kaynaklı zemin darbeleri, apartman içi konuşma ve gürültü problemleri için zemin şap altı ve duvar
              ara boşluklarında <Link to="/bondex-sunger-10mm" rel="follow">Bondex sünger 10mm</Link> veya{' '}
              <Link to="/bondex-sunger-20mm" rel="follow">Bondex sünger 20mm</Link> kalınlıkları tercih edilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Ofis ve Stüdyo Uygulamaları</h3>
            <p className={styles.seoText}>
              Söğütözü, Çankaya ve Kızılay gibi iş merkezlerindeki plaza ofisleri ve açık ofis kurulumlarında cam bölmeli alanlarda yankı ve ortam gürültüsü için bondex sünger katmanı,
              akustik paneller ve tekstil kaplamalar ile birlikte değerlendirilir. Kayıt stüdyoları, podcast odaları ve müzik stüdyolarında
              ses izolasyonu için <Link to="/bondex-sunger-30mm" rel="follow">Bondex sünger 30mm</Link> ve{' '}
              <Link to="/bondex-sunger-40mm" rel="follow">Bondex sünger 40mm</Link> kalınlıklar öne çıkar.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Endüstriyel Alanlar</h3>
            <p className={styles.seoText}>
              Jeneratör odası, makine dairesi ve soğutma grupları gibi endüstriyel alanlarda ise{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">Bariyerli Bondex Sünger</Link> ile kombine edilerek maksimum performans sağlanır.
            </p>

            <ul className={styles.seoList}>
              <li>Ankara konut projelerinde daire zeminleri, duvar ara boşlukları ve tavan detayları</li>
              <li>Çankaya, Keçiören, Yenimahalle, Mamak gibi merkezi ilçelerde plaza ve ofis uygulamaları</li>
              <li>Etimesgut, Sincan, Pursaklar gibi yoğun yerleşim bölgelerinde site projeleri</li>
              <li>Ankara merkez ve tüm ilçelerinde jeneratör odası ve makine dairesi izolasyonları</li>
              <li>Profesyonel kayıt stüdyoları, home ofis ve podcast odalarında akustik kontrol</li>
              <li>Kapı ve şaft detaylarında darbe ve hava kaynaklı sesi azaltmaya yönelik katmanlı sistemler</li>
            </ul>
          </section>

          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ankara İçin Doğru Bondex Sünger Seçimi</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Kalınlık Seçim Kriterleri</h3>
            <p className={styles.seoText}>
              Ankara projelerinde doğru <strong>bondex sünger kalınlığını</strong> seçmek, hem akustik performans hem de yapı statik
              sınırları açısından kritiktir. Eski yapı stoğunda gereksiz ağırlıktan kaçınmak için; hafif darbe sesleri ve ince detaylar için{' '}
              <Link to="/bondex-sunger-10mm" rel="follow">Bondex sünger 10mm</Link> veya{' '}
              <Link to="/bondex-sunger-20mm" rel="follow">Bondex sünger 20mm</Link> tercih edilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Yüksek Performans Gerektiren Projeler</h3>
            <p className={styles.seoText}>
              Daha agresif gürültü ve titreşimlerde <Link to="/bondex-sunger-30mm" rel="follow">Bondex sünger 30mm</Link>,{' '}
              <Link to="/bondex-sunger-40mm" rel="follow">Bondex sünger 40mm</Link> ve{' '}
              <Link to="/bondex-sunger-50mm" rel="follow">Bondex sünger 50mm</Link> kalınlıkları öne çıkar. Maksimum izolasyon hedeflerinde{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">Bariyerli Bondex Sünger</Link> ve{' '}
              <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> sistemleriyle katmanlı çözümler kurulur.
              Ankara'nın geniş yapı stoğu ve farklı ses problemleri göz önüne alındığında, proje bazlı teknik analiz ve doğru katman
              kurgusu büyük önem taşır. Detaylı seçim rehberi için{' '}
              <Link to="/blog-bondex-sunger" rel="follow">Bondex sünger blog yazımızı</Link> inceleyebilirsiniz.
            </p>
          </section>

          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ankara Bondex Sünger Fiyatları ve Sipariş</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Fiyatlandırma Bilgisi</h3>
            <p className={styles.seoText}>
              <strong>Ankara bondex sünger fiyatları</strong> metrekare (m²) başına 130 TL'den başlar ve seçilen kalınlık, toplam metraj,
              katman kurgusu ve proje özelliklerine göre değişir. Ankara içi projelerde stoktan hızlı sevkiyat avantajı sunulur; büyük
              metrajlı projelerde toplu alım indirimleri uygulanabilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Teklif Alma Süreci</h3>
            <p className={styles.seoText}>
              En doğru teklif için uygulama alanınızı (metraj), hedef izolasyon seviyenizi ve tercih ettiğiniz kalınlığı WhatsApp üzerinden
              paylaşmanız yeterlidir. Farklı kalınlık ve model alternatifleri için{' '}
              <Link to="/urunler" rel="follow">ürünler</Link> sayfamızı ziyaret edebilir,{' '}
              <Link to="/bondex-sunger" rel="follow">Bondex sünger</Link> ana sayfasından detaylı fiyat bilgisi alabilirsiniz.
            </p>
          </section>

          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ankara Teslimat ve Uygulama Desteği</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Teslimat Süreleri</h3>
            <p className={styles.seoText}>
              Ankara içi projelerde stoktan sevkiyat; genellikle 1-3 iş günü içinde gerçekleştirilir. Talebe göre aynı gün teslimat,
              hafta sonu veya mesai dışı montaj gibi farklı lojistik kurgular planlanabilir. Malzeme, şantiye erişimi ve asansör/lift
              kullanımına göre palet veya rulo formatında teslim edilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Hizmet Bölgeleri</h3>
            <p className={styles.seoText}>
              Ankara merkez ve tüm ilçelerine; Çankaya, Keçiören, Yenimahalle, Mamak, Etimesgut, Sincan, Pursaklar, Altındağ, Gölbaşı
              ve diğer bölgelere düzenli teslimat yapılır. Proje ölçülerinizi{' '}
              <Link to="/urunler" rel="follow">ürünler</Link> sayfasından sepet oluşturarak veya doğrudan WhatsApp üzerinden ileterek
              Ankara'ya özel teslimat planı oluşturabilirsiniz. Yerinde keşif, katman kurgusu danışmanlığı ve uygulama ekibi
              yönlendirmesi için teknik ekibimizle iletişime geçebilirsiniz.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler ve Malzeme Bilgisi</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Malzeme Yapısı ve Yoğunluk</h3>
            <p className={styles.seoText}>
              Ankara bondex sünger; geri dönüştürülmüş sünger parçacıklarının preslenmesiyle elde edilen{' '}
              <strong>rebonded süngerpan</strong> yapısına sahiptir. Yüksek yoğunluk değeri (80-120 kg/m³ arası) sayesinde, klasik akustik
              süngerlerden farklı olarak darbe ve titreşim enerjisini de sönümleyerek yapı ile ses kaynağı arasına tampon katman oluşturur.
              Lifli ve kompakt yapısı, hem hava kaynaklı sesleri hem de yapı titreşimlerini absorbe etmeye yardımcı olur. Ankara projeleri
              için 10mm, 20mm, 30mm, 40mm ve 50mm kalınlık seçenekleriyle stoktan tedarik edilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Uygulama Yüzeyleri ve Kombinasyonlar</h3>
            <p className={styles.seoText}>
              Ankara'daki projelerde bondex sünger; betonarme döşeme, çelik konstrüksiyon, alçıpan ara bölme, ahşap karkas ve metal
              profil sistemlerle birlikte kullanılır. Zemin detaylarında şap altında, duvar detaylarında metal karkas ve çift kat
              alçıpanla, kapı yalıtımında ise bariyerli kompozit panellerle kombin edilir. Daha yüksek performans gereken projelerde{' '}
              <Link to="/bariyerli-bondex-sunger-23mm" rel="follow">Bariyerli Bondex 23mm</Link>,{' '}
              <Link to="/bariyerli-bondex-sunger-33mm" rel="follow">33mm</Link> ve{' '}
              <Link to="/bariyerli-bondex-sunger-43mm" rel="follow">43mm</Link> gibi bariyerli modeller devreye girer. Maksimum izolasyon
              için <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex</Link> sistemleri tercih edilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Performans ve Hedef İzolasyon</h3>
            <p className={styles.seoText}>
              Hafif ve orta seviye darbe seslerinde 10-20mm, daha yüksek performans istendiğinde 30-50mm kalınlıklar kullanılır.
              Jeneratör odası ve makine dairesi gibi zorlu senaryolarda; bondex sünger, bariyerli ve çift bariyerli sistemlerle birlikte
              katmanlı olarak kurgulanarak hem darbe hem de hava kaynaklı sesi aynı anda hedefler. Ankara'nın geniş yapı stoğunda,
              özellikle apartman projeleri ve plaza ofislerinde, doğru kalınlık ve katman kombinasyonu ile ölçülebilir akustik iyileştirme
              sağlanır. Proje bazlı hedef dB değerleri için teknik ekibimizden detaylı hesap tablosu talep edebilirsiniz.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ankara'da Bondex Sünger Uygulama Alanları</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Konut ve Apartman Projeleri</h3>
            <p className={styles.seoText}>
              Ankara konut projelerinde; üst kat komşu gürültüsü, koridor sesleri, daireler arası konuşma sesleri ve çocuk/evcil hayvan
              kaynaklı darbe sesleri için bondex sünger zemin şap altında ve duvar ara boşluklarında kullanılır. Etimesgut, Sincan,
              Pursaklar gibi yeni yerleşim bölgelerindeki site projelerinde toplu uygulama avantajları; Çankaya, Keçiören, Yenimahalle gibi
              merkezi ilçelerdeki eski yapı stokunda ise renovasyon çözümleri sunulur.{' '}
              <Link to="/bondex-sunger-10mm" rel="follow">Bondex sünger 10mm</Link> ve{' '}
              <Link to="/bondex-sunger-20mm" rel="follow">Bondex sünger 20mm</Link> kalınlıklar ince detaylarda,{' '}
              <Link to="/bondex-sunger-30mm" rel="follow">Bondex sünger 30mm</Link> ve{' '}
              <Link to="/bondex-sunger-40mm" rel="follow">Bondex sünger 40mm</Link> kalınlıklar ise daha yüksek izolasyon hedeflerinde tercih edilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Ofis, Stüdyo ve Ticari Hacimler</h3>
            <p className={styles.seoText}>
              Ankara'daki plaza ofisleri, çağrı merkezleri, kayıt stüdyoları, podcast ve video içerik odaları gibi alanlarda; bondex
              sünger, akustik paneller ve tekstil kaplamalarla birlikte kullanılarak hem yankıyı hem de komşu hacimlere ses geçişini
              azaltır. Söğütözü, Kızılay, Çankaya gibi iş merkezlerinde açık ofis konseptlerinde ses kontrolü kritik öneme sahiptir.{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">Bariyerli Bondex Sünger</Link> ve{' '}
              <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> gibi kompozit ürünler, bu hacimlerdeki
              ileri seviye ihtiyaçlar için devreye alınır. Home ofis ve uzaktan çalışma alanlarında da Ankara bondex sünger pratik
              ve etkili bir çözüm sunar.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Endüstriyel Projeler ve Makine Hacimleri</h3>
            <p className={styles.seoText}>
              Ankara'daki jeneratör odaları, makine daireleri, soğutma grupları, havalandırma sistemleri ve endüstriyel üretim
              hatlarında; bondex sünger titreşim ve darbe kaynağı ile yapı arasına konumlandırılır. Organize sanayi bölgeleri, fabrika
              tesisleri ve lojistik merkezlerinde hem iç mekân konforu hem de komşu parsellerin korunması için katmanlı sistemler kurulur.
              Çoğu projede <Link to="/bariyerli-bondex-sunger" rel="follow">Bariyerli Bondex Sünger</Link> ve{' '}
              <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> sistemlerle birlikte kullanılarak
              kapsamlı akustik kontrol sağlanır. Detaylı örnekler ve uygulama senaryoları için{' '}
              <Link to="/blog-bondex-sunger" rel="follow">Bondex sünger blog yazımızı</Link> inceleyebilirsiniz.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          {/* H2: Ana Bölüm */}
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Ek Hizmetler ve Bilgiler</h2>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Ankara İçi Lojistik ve Montaj Opsiyonları</h3>
            <p className={styles.seoText}>
              Ankara içi projelerde; stoktan sevkiyat, etaplı teslimat ve hafta sonu/mesai dışı montaj gibi farklı lojistik kurgular
              planlanabilir. Malzeme, şantiye erişimi ve asansör/lift kullanımına göre palet veya rulo formatında teslim edilir. Ankara
              trafiği ve şehir şartları göz önüne alınarak optimum teslimat saatleri belirlenir. Büyük metrajlı projelerde forklift ile
              boşaltma, küçük projelerde el ile taşıma seçenekleri mevcuttur. Proje ölçülerinizi{' '}
              <Link to="/urunler" rel="follow">ürünler</Link> sayfasından sepet oluşturarak veya doğrudan WhatsApp üzerinden ilettiğinizde;
              Ankara'ya özel teslimat planı birlikte netleştirilir.
            </p>

            {/* H3: Alt Başlık */}
            <h3 className={styles.seoSubtitle}>Diğer Şehirler ve Model Karşılaştırmaları</h3>
            <p className={styles.seoText}>
              Ankara dışındaki projeleriniz için de aynı kalitede hizmet sunuyoruz. İstanbul, İzmir, Antalya, Bursa, Kocaeli, Mersin ve
              Adana gibi büyük şehirlere de düzenli sevkiyat yapılmaktadır. Farklı kalınlık ve model seçenekleri için{' '}
              <Link to="/bondex-sunger" rel="follow">Bondex Sünger</Link>,{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">Bariyerli Bondex Sünger</Link> ve{' '}
              <Link to="/cift-bariyerli-bondex-sunger" rel="follow">Çift Bariyerli Bondex Sünger</Link> ürün sayfalarından model karşılaştırması
              yapabilirsiniz. Her projeye özel teknik danışmanlık ve fiyat teklifi için WhatsApp üzerinden iletişime geçebilirsiniz.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      {/* SEO Meta ve Schema */}
      <JsonLd data={schema} />

      {/* Breadcrumb */}
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Ankara Bondex Sünger</span>
      </nav>

      {/* Hero Grid - ProductDetail ile aynı yapı */}
      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img
            className={styles.img}
            src={ankProduct.imagePath}
            alt="Ankara bondex sünger uygulaması"
            loading="eager"
            width="600"
            height="400"
          />
        </div>

        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Ankara Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-ANK | Ankara İçi Projeler
          </p>
          <p className={styles.meta}>
            Ankara'da <strong>bondex sünger</strong> arayan konut, ofis, stüdyo ve endüstriyel projeler için; stoktan hızlı sevkiyat,
            yerinde keşif ve akustik danışmanlığı tek çatı altında topluyoruz. Yüksek yoğunluklu <strong>rebonded süngerpan</strong> yapısı;
            darbe ve titreşim seslerini azaltmaya yardımcı olurken, Ankara'nın geniş yapı stoğuna ve karma ses problemlerine göre projeye
            özel katman kurguları oluşturuyoruz.
          </p>

          {/* Rating */}
          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {/* Fiyat */}
          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          {/* Badges */}
          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Ankara içi hızlı teslimat</span>
            <span className="chip">Yerinde keşif ve uygulama</span>
          </div>

          {/* Spec Grid */}
          <div className={styles.specGrid} aria-label="Ankara için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      {/* Sekmeli İçerik */}
      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {/* Değerlendirmeler */}
      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h4 className={styles.reviewName}>{review.title}</h4>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {ankProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Ankara Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={ankProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {/* İlgili Ürünler - İç Link Amaçlı */}
      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}

